import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Globe, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { useCategoryHelpers } from '@/hooks/useCategoryHelpers';
import { supabase } from '@/integrations/supabase/client';

interface ProfessionalCardProps {
  data: {
    id: string;
    slug: string;
    business_name: string;
    first_name?: string;
    last_name?: string;
    categories?: string[];
    address_city?: string;
    address_region?: string;
    profile_image?: string;
    is_verified: boolean;
    languages_spoken?: string[];
    distance?: number;
    score?: number;
    is_featured?: boolean;
    feature_type?: 'spotlight' | 'social_boost';
    average_rating?: number;
    review_count?: number;
    subscription_type?: 'base' | 'pro' | 'plus' | null;
    is_company?: boolean;
    tagline_it?: string;
    tagline_en?: string;
    tagline_es?: string;
  };
  showDistance?: boolean;
  isFeatured?: boolean;
  layout?: 'vertical' | 'horizontal';
}

export const ProfessionalCard = ({ data, showDistance = false, isFeatured = false, layout = 'vertical' }: ProfessionalCardProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const { getCategoriesByIds, isLoading: categoriesLoading, isReady: categoriesReady, categories } = useCategoryHelpers();

  const handleClick = async () => {
    // Track featured click if applicable (only for professionals, not companies)
    if (data.is_featured && !data.is_company) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Check if user is the owner
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('id', data.id)
          .single();
        
        // Track only if not the owner
        if (!profile || profile.user_id !== user?.id) {
          await supabase.functions.invoke('track-featured-click', {
            body: { profile_id: data.id }
          });
        }
      } catch (error) {
        console.error('Error tracking click:', error);
      }
    }
    
    // Navigate to appropriate page based on type
    if (data.is_company) {
      navigate(`/company/${data.slug}`);
    } else {
      navigate(`/professional/${data.slug}`);
    }
  };
  
  const currentLanguage = (i18n.language || 'it').substring(0, 2) as 'it' | 'en' | 'es';
  
  // Use useMemo to reactively update when categories load
  const categoryData = useMemo(() => {
    if (!categoriesReady || !data.categories?.length) return [];
    return getCategoriesByIds(data.categories);
  }, [categoriesReady, data.categories, getCategoriesByIds, categories]);

  return (
    <div
      className={`border-[3px] border-dark bg-card shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] transition-all cursor-pointer overflow-hidden h-full ${
        isFeatured ? 'ring-4 ring-yellow-400' : ''
      } ${
        layout === 'horizontal' ? 'flex flex-col md:flex-row md:min-h-[240px]' : 'flex flex-col'
      }`}
      onClick={handleClick}
    >
      {/* Image Section with Skeleton */}
      <div className={
        layout === 'horizontal'
          ? 'relative w-full md:w-64 h-48 md:h-full md:border-r-[3px] border-b-[3px] md:border-b-0 border-dark flex-shrink-0'
          : 'relative w-full aspect-square border-b-[3px] border-dark'
      }>
        {data.profile_image ? (
          <>
            {/* Skeleton while loading */}
            {!imageLoaded && (
              <Skeleton className="absolute inset-0 w-full h-full" />
            )}
            <img
              src={data.profile_image}
              alt={data.business_name}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
            />
          </>
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <span className="text-6xl">
              {categoryData[0]?.icon || data.business_name[0]}
            </span>
          </div>
        )}
      </div>
      
      <div className="p-4 flex-1 flex flex-col">
        {/* Content wrapper - takes remaining space */}
        <div className="space-y-3 flex-1 pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <h3 className="font-heading font-bold text-lg truncate max-w-[70%]">
                  {data.is_company 
                    ? data.business_name
                    : (data.first_name && data.last_name 
                        ? `${data.first_name} ${data.last_name.charAt(0)}.` 
                        : data.business_name)
                  }
                </h3>
                {data.is_company && (
                  <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] px-1.5 py-0.5 border-0">
                    {t('professionals.companyBadge')}
                  </Badge>
                )}
                {!data.is_company && data.subscription_type === 'plus' && (
                  <Badge className="bg-purple-500 text-white text-[10px] px-1.5 py-0.5 border-0">
                    PLUS
                  </Badge>
                )}
                {!data.is_company && data.subscription_type === 'pro' && (
                  <Badge className="bg-blue-500 text-white text-[10px] px-1.5 py-0.5 border-0">
                    PRO
                  </Badge>
                )}
              </div>
              {!data.is_company && data.first_name && data.last_name && (
                <p className="text-sm text-muted-foreground truncate">
                  {data.business_name}
                </p>
              )}
              {data.is_company && (data.tagline_it || data.tagline_en || data.tagline_es) && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {currentLanguage === 'it' 
                    ? data.tagline_it 
                    : currentLanguage === 'en' 
                      ? (data.tagline_en || data.tagline_it)
                      : (data.tagline_es || data.tagline_it)}
                </p>
              )}
            </div>
          </div>

          {data.categories && data.categories.length > 0 && (
            <div className="text-xs text-muted-foreground">
              {categoriesLoading ? (
                <Skeleton className="h-3 w-32" />
              ) : (
                categoryData.map((cat, index) => {
                  const catName = cat?.[currentLanguage]?.name || cat?.it?.name || cat?.id;
                  return (
                    <span key={cat.id}>
                      {catName}
                      {index < categoryData.length - 1 && ', '}
                    </span>
                  );
                })
              )}
            </div>
          )}

          <div className="text-sm space-y-2">
            {data.address_region && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{data.address_region}</span>
              </div>
            )}

            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-4 h-4 ${
                    star <= Math.round(data.average_rating || 0)
                      ? 'fill-dark text-dark'
                      : 'fill-none text-dark stroke-[1.5]'
                  }`}
                />
              ))}
              <span className="text-xs text-muted-foreground ml-1">
                ({data.review_count || 0})
              </span>
            </div>

            {showDistance && data.distance !== undefined && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>{data.distance.toFixed(1)} km</span>
              </div>
            )}

            {data.languages_spoken && data.languages_spoken.length > 0 && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  {data.languages_spoken.slice(0, 2).join(', ')}
                  {data.languages_spoken.length > 2 && ` +${data.languages_spoken.length - 2}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Button always at bottom */}
        <BrutalButton 
          size="sm" 
          className="w-full mt-auto"
        >
          {data.is_company ? t('professionals.viewCompany') : t('professionals.viewProfile')}
        </BrutalButton>
      </div>
    </div>
  );
};
